import React, { ReactNode } from 'react';
import { AppError } from '@repo/i18n';
import { toAppError } from '@repo/api-client';
import { ErrorState } from './ErrorState';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';

export type AsyncView<T> =
  | { status: 'loading' }
  | { status: 'error'; error: AppError; retry?: () => void }
  | { status: 'empty'; reason: 'no-data' | 'filtered' }
  | { status: 'ready'; data: T };

export interface QueryState<T> {
  isLoading: boolean;
  isError: boolean;
  error?: any;
  data?: T;
  refetch?: () => void;
}

interface DataViewProps<T> {
  query?: QueryState<T>;
  view?: AsyncView<T>;
  children: (data: NonNullable<T>) => ReactNode;
  loadingSkeleton?: ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: { label: string; onClick: () => void };
  isEmpty?: (data: NonNullable<T>) => boolean;
}

function defaultIsEmpty<T>(data: NonNullable<T>): boolean {
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === 'object' && data !== null) return Object.keys(data).length === 0;
  return false;
}

export function DataView<T>({
  query,
  view: providedView,
  children,
  loadingSkeleton = <Skeleton rows={5} />,
  emptyTitle = 'No data',
  emptyDescription,
  emptyAction,
  isEmpty,
}: DataViewProps<T>) {
  let view: AsyncView<T>;

  if (providedView) {
    view = providedView;
  } else if (query) {
    if (query.isLoading) {
      view = { status: 'loading' };
    } else if (query.isError) {
      const appError: AppError = query.error?.category
        ? query.error
        : toAppError(query.error);
      view = { status: 'error', error: appError, retry: query.refetch };
    } else if (
      query.data === undefined ||
      query.data === null ||
      (Array.isArray(query.data) && query.data.length === 0) ||
      (isEmpty && isEmpty(query.data)) ||
      defaultIsEmpty(query.data)
    ) {
      view = { status: 'empty', reason: 'no-data' };
    } else {
      view = { status: 'ready', data: query.data };
    }
  } else {
    return null;
  }

  switch (view.status) {
    case 'loading':
      return <>{loadingSkeleton}</>;
    case 'error':
      return <ErrorState error={view.error} retry={view.retry} />;
    case 'empty':
      return (
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          variant={view.reason}
          action={emptyAction}
        />
      );
    case 'ready':
      return <>{children(view.data as NonNullable<T>)}</>;
    default:
      return null;
  }
}
